<script setup lang="ts">
import { t } from "../i18n";
import { ref, watch, nextTick, onBeforeUnmount } from "vue";
import IconButton from "./IconButton.vue";
import { useEscapeLayer } from "../composables/escape";
const props = defineProps<{
  open: boolean;
  title: string;
  wide?: boolean;
  /** WhatsApp's question box: the title centred, no close button, the answers stacked below. */
  alert?: boolean;
}>();
const emit = defineEmits<{ close: [] }>();
const dialog = ref<HTMLElement>();
useEscapeLayer(
  () => props.open,
  () => emit("close"),
);
let previous: HTMLElement | null = null;
function key(e: KeyboardEvent) {
  if (e.key === "Tab") {
    const els = dialog.value?.querySelectorAll<HTMLElement>(
      'button:not(:disabled),a[href],input:not(:disabled),textarea:not(:disabled),[tabindex="0"]',
    );
    if (!els?.length) return;
    const first = els[0],
      last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}
watch(
  () => props.open,
  async (v) => {
    if (v) {
      previous = document.activeElement as HTMLElement;
      await nextTick();
      if (!props.open) return;
      (
        dialog.value?.querySelector<HTMLElement>("input,textarea") ||
        dialog.value?.querySelector<HTMLElement>("button")
      )?.focus();
      document.addEventListener("keydown", key, true);
    } else {
      document.removeEventListener("keydown", key, true);
      previous?.focus();
    }
  },
  { immediate: true },
);
onBeforeUnmount(() => document.removeEventListener("keydown", key, true));
</script>
<template>
  <Teleport to="body"
    ><Transition name="fade"
      ><div v-if="open" class="modal-overlay" @mousedown.self="emit('close')">
        <section
          ref="dialog"
          class="wa-modal"
          :class="{ wide, alert }"
          :role="alert ? 'alertdialog' : 'dialog'"
          aria-modal="true"
          :aria-label="title"
        >
          <header>
            <h2>{{ title }}</h2>
            <slot name="header" />
            <IconButton
              v-if="!alert"
              class="modal-close"
              icon="close"
              :label="t('common.close')"
              @click="emit('close')"
            />
          </header>
          <div v-if="$slots.default" class="modal-body scroll"><slot /></div>
          <footer v-if="$slots.footer"><slot name="footer" /></footer>
        </section></div></Transition
  ></Teleport>
</template>
